import { useMemo, useState } from "react";
import { notification, Select, Space } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@rumbledirect/ui";
import type { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import {
  fetchServicepartnersWithoutPagination,
  type ServicePartnerBrief,
} from "@/lib/api/resources/servicePartners";
import { sendDossierToServicePartner } from "@/lib/api/resources/dossiers";

interface SendToServicePartnerActionProps {
  dossier: Dossier | undefined;
}

export const SendToServicePartnerAction = ({
  dossier,
}: SendToServicePartnerActionProps) => {
  const queryClient = useQueryClient();
  const [selectedServicePartnerId, setSelectedServicePartnerId] = useState<
    string | undefined
  >(undefined);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const hasPhotos = dossier?.hasAttachablePhotos ?? false;

  const {
    data: servicePartners,
    isLoading: isServicePartnersLoading,
    isFetching: isServicePartnersFetching,
  } = useQuery({
    queryKey: ["servicePartners", "brief"],
    queryFn: () => fetchServicepartnersWithoutPagination(),
  });

  // The brief endpoint currently exposes no server-side filter, so we apply a
  // simple case-insensitive substring filter on the search term to keep the
  // list usable while typing.
  const options = useMemo(() => {
    const list: ServicePartnerBrief[] = Array.isArray(servicePartners)
      ? servicePartners
      : [];

    const needle = searchTerm.trim().toLowerCase();
    const filtered = needle
      ? list.filter((sp) => {
          const haystack = [sp.fullName, sp.name, sp.city, sp.groupName]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(needle);
        })
      : list;

    return filtered.map((sp) => ({
      value: sp.id as unknown as string,
      label: sp.city ? `${sp.fullName} (${sp.city})` : sp.fullName,
    }));
  }, [servicePartners, searchTerm]);

  const sendMutation = useMutation({
    mutationFn: () => {
      if (!dossier) {
        throw new Error("No dossier provided");
      }
      if (!selectedServicePartnerId) {
        throw new Error("No service partner selected");
      }
      return sendDossierToServicePartner({
        dossierId: dossier.id,
        servicePartnerId: selectedServicePartnerId,
      });
    },
    onSuccess: () => {
      notification.success({
        message: "E-mail verstuurd",
        description: "De e-mail naar de gekozen hersteller wordt verwerkt.",
      });
      setSelectedServicePartnerId(undefined);
      if (dossier) {
        queryClient.invalidateQueries({ queryKey: ["dossier", dossier.id] });
      }
    },
    onError: () => {
      notification.error({
        message: "Versturen mislukt",
        description: "Er ging iets mis bij het versturen van de e-mail.",
      });
    },
  });

  return (
    <Space direction="vertical" size="middle" className="w-full">
      <p className="mb-0">
        Stuur een verzoek tot herstel met fotobijlagen naar een gekozen
        hersteller.
      </p>
      {dossier && !hasPhotos ? (
        <p className="mb-0 text-red-600">
          Er kan geen e-mail naar de hersteller worden verstuurd omdat er geen
          foto's aanwezig zijn.
        </p>
      ) : null}
      <Select
        showSearch
        allowClear
        filterOption={false}
        onSearch={setSearchTerm}
        onClear={() => setSearchTerm("")}
        loading={isServicePartnersLoading || isServicePartnersFetching}
        placeholder="Selecteer een hersteller"
        options={options}
        value={selectedServicePartnerId}
        onChange={(value: string | undefined) =>
          setSelectedServicePartnerId(value)
        }
        className="w-full"
        disabled={!dossier || !hasPhotos || sendMutation.isPending}
      />
      <Button
        type="primary"
        size="large"
        className="w-full"
        disabled={!selectedServicePartnerId || !dossier || !hasPhotos}
        loading={sendMutation.isPending}
        onClick={() => sendMutation.mutate()}
      >
        E-mail naar hersteller versturen
      </Button>
    </Space>
  );
};
